import ApiDatabase from '../services/ApiDatabase.js';
import DeviceManager from '../utils/DeviceManager.js';

export default class AuthManager {
    constructor(gameEngine) {
        this.engine = gameEngine;
        this.useApi = true;
        this.db = new ApiDatabase();
        this.currentUser = null;
        this.inventory = [];
    }

    get token() {
        return this.db.token;
    }

    async init() {
        // Build Landing UI listeners
        const landingPlay = document.getElementById('landing-play-btn');
        if (landingPlay) landingPlay.onclick = () => this.registerGuest();
        
        const landingLogin = document.getElementById('landing-login-btn');
        if (landingLogin) landingLogin.onclick = () => this.engine.showLoginScreen();

        const replayTutorial = document.getElementById('replay-tutorial-btn');
        if (replayTutorial) replayTutorial.onclick = () => {
            if (window.onboarding) {
                this.engine.hideAllScreens();
                this.engine.ui.menu.classList.remove('hidden');
                window.onboarding.start(true); // Forced replay
            }
        };

        // Load dynamic economy items from server early
        await this.db.loadCatalog();
        if (this.engine.themeManager) {
            await this.engine.themeManager.loadFromServer(this.db.baseUrl);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const resetToken = urlParams.get('resetToken');
        if (resetToken) {
            this.engine.pendingPasswordResetToken = resetToken;
            this.engine.showResetPasswordScreen();
            return;
        }

        const savedSession = localStorage.getItem('pixelblob_session_token') || localStorage.getItem('quicky_session_token');
        if (savedSession) {
            await this.restoreSession();
        } else {
            console.log("[AuthManager] No session found. Showing Landing Screen...");
            this.engine.hideAllScreens();
            document.getElementById('landing-screen').classList.remove('hidden');
            document.getElementById('landing-screen').classList.add('active');
        }
        this.checkStripeRedirect();
    }

    async registerGuest() {
        this.engine.showNotification(this.engine.i18n.t('notifications.creatingGuest'));
        try {
            const deviceId = DeviceManager.getDeviceId();
            const fingerprint = DeviceManager.getFingerprint();
            const user = await this.db.registerGuest(deviceId, fingerprint);
            if (user) {
                this.setCurrentUser(user);
                this.engine.showMainMenu();
                if (window.onboarding) window.onboarding.start();
            }
        } catch (e) {
            console.error("Guest registration failed", e);
            this.engine.showLoginScreen();
            this.engine.showNotification(this.engine.i18n.t('notifications.guestCreationFailed'), 3000);
        }
    }

    async checkStripeRedirect() {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const productId = urlParams.get('product');

        if (sessionId) {
            window.history.replaceState({}, document.title, window.location.pathname);
            this.engine.showNotification(this.engine.i18n.t('notifications.verifyingPurchase'));
            try {
                const res = await this.db.authenticatedFetch('/economy/verify-session', {
                    method: 'POST',
                    body: JSON.stringify({ sessionId })
                });
                const data = await res.json();
                if (data.success) {
                    this.engine.showNotification(data.message || "Purchase Successful!");
                    const user = await this.db.getUser();
                    if (user) this.setCurrentUser(user);

                    if (productId === 'premium') {
                        this.engine.showNotification(this.engine.i18n.t('notifications.premiumActivated'));
                    } else {
                        const amount = productId.split('_')[0];
                        if (!isNaN(amount)) this.engine.showNotification(`+${amount} COINS! 💰`);
                    }
                } else {
                    this.engine.showNotification("Purchase Verification Failed: " + data.message);
                }
            } catch (e) {
                console.error("Verification Error", e);
                this.engine.showNotification("Network Error verifying purchase.");
            }
        } else if (urlParams.get('canceled')) {
            window.history.replaceState({}, document.title, window.location.pathname);
            this.engine.showNotification(this.engine.i18n.t('notifications.purchaseCanceled'));
        }
    }

    async restoreSession() {
        try {
            const user = await this.db.getUser();
            if (user) {
                this.setCurrentUser(user);
                if (this.engine.challengeManager) {
                    this.engine.challengeManager.onUserChanged();
                }
                this.engine.showMainMenu();
            }
        } catch (e) {
            console.error("Session restore failed", e);
            localStorage.removeItem('pixelblob_session_token');
            localStorage.removeItem('quicky_session_token');
        }
    }

    async login(email, password) {
        try {
            await this.db.loginUser(email, password);
            const user = await this.db.getUser();
            this.setCurrentUser(user);
            return { success: true };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    async register(username, email, password) {
        try {
            await this.db.createUser(username, email, password);
            const user = await this.db.getUser();
            this.setCurrentUser(user);
            return { success: true };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    async requestPasswordReset(email) {
        try {
            const result = await this.db.requestPasswordReset(email);
            return { success: true, message: result.message };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    async resetPassword(token, password) {
        try {
            const result = await this.db.resetPassword(token, password);
            return { success: true, message: result.message };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    async convertGuest(username, email, password) {
        try {
            await this.db.convertGuest(username, email, password);
            const user = await this.db.getUser();
            this.setCurrentUser(user);
            return { success: true };
        } catch (error) {
            return { success: false, message: error.message };
        }
    }

    setCurrentUser(user) {
        // Map snake_case to camelCase consistently
        if (user.is_guest !== undefined) user.isGuest = user.is_guest;
        if (user.is_email_verified !== undefined) user.isEmailVerified = user.is_email_verified;
        if (user.max_energy !== undefined) user.maxEnergy = user.max_energy;

        // Welcome Gift & Undefined fix for new users
        if ((user.coins === undefined || user.coins === 0) && (!user.xp || user.xp < 10)) {
            user.coins = 50;
        }
        // Force 10/10 for new users even if they had 20 before
        if ((!user.energy || user.energy === 0 || user.energy > 10) && (!user.xp || user.xp < 10)) {
            user.energy = 10;
        }
        if (user.maxEnergy === undefined || user.maxEnergy === null || user.maxEnergy > 10) {
            if (!user.xp || user.xp < 10) user.maxEnergy = 10;
        }

        this.currentUser = user;
        this.refreshInventory();
        console.log('Logged in as:', user.username, user.isGuest ? "(GUEST)" : "(MEMBER)");
        
        // Handle Guest Banner
        const banner = document.getElementById('guest-banner');
        if (banner) {
            if (user.isGuest) {
                banner.classList.remove('hidden');
                
                // Add button listeners
                const regBtn = document.getElementById('guest-register-btn');
                regBtn.onclick = () => {
                    this.engine.showConvertScreen();
                    banner.classList.add('hidden');
                };
                
                const closeBtn = document.getElementById('close-guest-banner');
                closeBtn.onclick = () => banner.classList.add('hidden');
            } else {
                banner.classList.add('hidden');
            }
        }

        // Handle Verification Banner
        const verifyBanner = document.getElementById('verify-email-banner');
        if (verifyBanner) {
            if (!user.isGuest && !user.isEmailVerified) {
                verifyBanner.classList.remove('hidden');
                
                const resendBtn = document.getElementById('resend-verify-btn');
                resendBtn.onclick = async () => {
                    resendBtn.disabled = true;
                    resendBtn.textContent = "SENDING...";
                    try {
                        const res = await this.db.resendVerification();
                        this.engine.showNotification(res.message);
                        resendBtn.textContent = "SENT!";
                    } catch (e) {
                        this.engine.showNotification(e.message);
                        resendBtn.textContent = "ERROR";
                        resendBtn.disabled = false;
                    }
                };

                const closeVerify = document.getElementById('close-verify-banner');
                closeVerify.onclick = () => verifyBanner.classList.add('hidden');
            } else {
                verifyBanner.classList.add('hidden');
            }
        }

        this.engine.score = 0;
        this.engine.updateHUD();

        if (user.selectedTheme) {
            if (this.engine.themeManager.themes[user.selectedTheme]) {
                this.engine.themeManager.applyTheme(user.selectedTheme);
            }
        }
        if (user.selectedEffect) {
            if (this.engine.themeManager.effects[user.selectedEffect]) {
                this.engine.themeManager.applyEffect(user.selectedEffect);
            }
        }

        if (this.engine.challengeManager) {
            this.engine.challengeManager.onUserChanged();
        }
    }

    refreshInventory() {
        if (!this.currentUser) return;
        this.inventory = [];
        if (this.currentUser.unlockedGames) {
            this.inventory.push(...this.currentUser.unlockedGames);
        }
    }

    async unlockGame(gameName) {
        if (!this.currentUser) return false;
        if (this.inventory.includes(gameName)) return false;

        this.inventory.push(gameName);
        if (!this.currentUser.unlockedGames) this.currentUser.unlockedGames = [];
        this.currentUser.unlockedGames.push(gameName);

        try {
            await this.db.purchaseItem(gameName, 0, 'GAME');
            return true;
        } catch (e) {
            console.warn("Failed to save unlock", e);
            return true;
        }
    }

    logout() {
        this.currentUser = null;
        this.inventory = [];
        if (this.db.logout) this.db.logout();
        if (this.engine.challengeManager) {
            this.engine.challengeManager.onUserChanged();
        }
        this.engine.showLoginScreen();
    }

    async spendCoins(amount, reason = 'CONSUMABLE') {
        if (!this.currentUser || !this.canAfford(amount)) return false;

        // Optimistic UI update
        this.currentUser.coins -= amount;
        this.engine.updateHUD();

        try {
            // Try to sync with server using the buy endpoint
            const res = await this.db.purchaseItem(reason, amount, 'CONSUMABLE');
            if (res && res.newBalance !== undefined) {
                this.currentUser.coins = res.newBalance;
                this.engine.updateHUD();
            }
            return true;
        } catch (e) {
            console.warn("Failed to sync spend with server", e);
            return false;
        }
    }

    async submitGameResult(gameKey, score, duration, roundsWon) {
        if (!this.currentUser) return;
        try {
            const result = await this.db.submitGameResult(gameKey, score, duration, roundsWon);
            if (result) {
                this.currentUser.coins = result.newBalance;
                this.currentUser.xp = result.newXp;
                if (result.newLevel) this.currentUser.level = result.newLevel;
                this.engine.updateHUD();
            }
            return result;
        } catch (e) {
            console.error("Game submission failed", e);
            return null;
        }
    }

    async updateScoreTag(scoreId, tag) {
        return await this.db.updateScoreTag(scoreId, tag);
    }

    hasItem(itemId) {
        return this.inventory.includes(itemId);
    }

    canAfford(price) {
        return this.currentUser && this.currentUser.coins >= price;
    }

    async purchaseItem(itemId) {
        let price = 999999;
        let type = itemId.includes('effect') ? 'EFFECT' : 'THEME';

        const catItem = this.db.catalog.find(i => i.id === itemId);
        if (catItem) {
            price = catItem.price;
            type = catItem.type.toUpperCase();
        } else {
            // Fallback against browser caching bypassing the catalog update
            if (this.engine.themeManager.themes[itemId]) {
                price = this.engine.themeManager.themes[itemId].price || 0;
                type = 'THEME';
            } else if (this.engine.themeManager.effects[itemId]) {
                price = this.engine.themeManager.effects[itemId].price || 0;
                type = 'EFFECT';
            }
        }

        if (!this.canAfford(price)) {
            console.warn(`[AuthManager] canAfford failed. Price: ${price}, Coins: ${this.currentUser?.coins}`);
            return false;
        }

        try {
            const res = await this.db.purchaseItem(itemId, price, type);
            this.currentUser.coins = res.newBalance;
            this.inventory.push(itemId);
            this.engine.updateHUD();
            return true;
        } catch (e) {
            console.warn("Purchase failed", e);
            return false;
        }
    }

    async savePreferences(themeId, effectId) {
        if (!this.currentUser) return;
        await this.db.updateUserPreferences(this.currentUser.id, themeId, effectId);
    }

    earnXp(amount) { }

    getXpForNextLevel(level) {
        return Math.floor(100 * Math.pow(1.1, level - 1));
    }

    isGameUnlocked(gameName) {
        return this.inventory.includes(gameName);
    }

    getFriendCode() {
        return this.currentUser ? this.currentUser.friendCode : null;
    }

    async getFriends() {
        if (!this.currentUser) return [];
        return await this.db.getFriends();
    }

    async addFriend(friendCode) {
        if (!this.currentUser) return { success: false, message: "Not logged in" };
        return await this.db.addFriend(friendCode);
    }

    updatePersonalBest(gameKey, score) {
        if (!this.currentUser) return;
        if (!this.currentUser.personalBests) this.currentUser.personalBests = {};
        const current = this.currentUser.personalBests[gameKey] || 0;
        if (score > current) {
            this.currentUser.personalBests[gameKey] = score;
            return true;
        }
        return false;
    }

    async savePromotionLink(link) {
        try {
            const result = await this.db.updatePromotionLink(link);
            if (this.currentUser) this.currentUser.promotionLink = link;
            return { success: true };
        } catch (e) {
            return { success: false, message: e.message };
        }
    }

    async getUserRanks(gameKey) {
        if (!this.currentUser) return null;
        return await this.db.getUserRanks(gameKey);
    }

    async getUserBestScores() {
        if (!this.currentUser) return {};
        return await this.db.getUserBestScores();
    }

    async getEnergyStatus() {
        if (!this.currentUser) return null;
        try {
            const res = await this.db.authenticatedFetch('/economy/energy');
            if (res.ok) {
                const data = await res.json();
                this.currentUser.energy = data.current;
                this.currentUser.maxEnergy = data.max;
                this.currentUser.nextRefillIn = data.nextIn;
                this.engine.updateHUD();
                return data;
            }
        } catch (e) {
            console.error("Failed to fetch energy", e);
        }
        return null;
    }

    async startGame() {
        if (!this.currentUser) return { success: true };
        try {
            const res = await this.db.authenticatedFetch('/economy/start', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                this.currentUser.energy = data.energy;
                this.engine.updateHUD();
                return { success: true };
            } else {
                return { success: false, message: data.message, nextIn: data.nextIn };
            }
        } catch (e) {
            console.error("Start Game Error", e);
            return { success: false, message: "Connection Error" };
        }
    }

    async startStripeCheckout(productId) {
        if (!this.currentUser) {
            this.engine.showNotification("Please Login First!");
            this.engine.showLoginScreen();
            return;
        }

        this.engine.showNotification("Redirecting to Stripe...");

        try {
            const res = await this.db.authenticatedFetch('/economy/create-checkout-session', {
                method: 'POST',
                body: JSON.stringify({ productId })
            });

            const data = await res.json();

            if (data.url) {
                window.location.href = data.url;
            } else {
                this.engine.showNotification("Checkout Error: " + (data.error || "Unknown"));
            }
        } catch (e) {
            console.error("Checkout Request Failed", e);
            this.engine.showNotification("Connection Error");
        }
    }

    async buyPremium() {
        await this.startStripeCheckout('premium');
    }

    async buyPack(packId) {
        await this.startStripeCheckout(packId);
    }
}
